import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  MessageSquare,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Hash,
  Clock,
  Search,
  Info,
} from 'lucide-react';

import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatPhoneNumber = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

const toE164 = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (trimmed.startsWith('+')) {
    return '+' + trimmed.slice(1).replace(/\D/g, '');
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits ? `+${digits}` : '';
};

const formatDuration = (seconds) => {
  const s = Number(seconds) || 0;
  if (s <= 0) return '—';
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
};

const ActivityRow = ({ item }) => {
  const isCall = item.type === 'call';
  const isInbound = (item.direction || '').toLowerCase() === 'incoming';
  const Icon = isCall ? (isInbound ? PhoneIncoming : PhoneOutgoing) : MessageSquare;
  const accent = isCall
    ? isInbound
      ? 'bg-blue-100 text-blue-600'
      : 'bg-emerald-100 text-emerald-600'
    : 'bg-violet-100 text-violet-600';

  return (
    <div
      className="flex items-start gap-4 p-4 border-b border-slate-100 last:border-b-0"
      data-testid={`comms-activity-row-${item.id}`}
    >
      <div className={`p-2 rounded-lg flex-shrink-0 ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800 text-sm">
            {formatPhoneNumber(item.external_number) || 'Unknown'}
          </span>
          <Badge variant="outline" className="text-xs capitalize">
            {item.direction || 'unknown'}
          </Badge>
          {item.status && (
            <Badge variant="secondary" className="text-xs capitalize">
              {item.status}
            </Badge>
          )}
          {item.phone_number_label && (
            <span className="text-xs text-slate-400 inline-flex items-center gap-1">
              <Hash className="w-3 h-3" />
              {item.phone_number_label}
            </span>
          )}
        </div>
        {item.preview && (
          <p className="text-sm text-slate-600 mt-1 break-words">{item.preview}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatRelative(item.created_at)}
          </span>
          {isCall && item.duration > 0 && <span>· {formatDuration(item.duration)}</span>}
        </div>
      </div>
    </div>
  );
};

const AdminPhonePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);

  const [lookupInput, setLookupInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [filter, setFilter] = useState('all'); // all | calls | messages

  useEffect(() => {
    if (!localStorage.getItem('adminUser')) {
      navigate('/admin/login');
      return;
    }
    loadStatus().finally(() => setLoading(false));
  }, [navigate]);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quo/status`);
      setStatus(res.data);
    } catch (err) {
      console.error('quo/status error', err);
      setStatus({ configured: false, connected: false, phone_numbers: [] });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadStatus();
      toast.success('Refreshed connection status');
    } finally {
      setRefreshing(false);
    }
  };

  const handleLookup = async (e) => {
    e?.preventDefault?.();
    const e164 = toE164(lookupInput);
    if (!e164 || e164.length < 7) {
      toast.error('Enter a phone number in E.164 (+15551234567) or US format');
      return;
    }
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/quo/lookup`, {
        params: { participant: e164, days: 180 },
      });
      setLookupResult(res.data);
      if ((res.data?.data || []).length === 0) {
        toast.info(`No call/SMS history with ${formatPhoneNumber(e164)} in the last 180 days`);
      }
    } catch (err) {
      console.error('quo/lookup error', err);
      toast.error(err.response?.data?.detail || 'Lookup failed');
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const filteredFeed = useMemo(() => {
    const data = lookupResult?.data || [];
    if (filter === 'all') return data;
    if (filter === 'calls') return data.filter((i) => i.type === 'call');
    if (filter === 'messages') return data.filter((i) => i.type === 'message');
    return data;
  }, [lookupResult, filter]);

  const isConfigured = !!status?.configured;
  const isConnected = !!status?.connected;
  const quoWebUrl = status?.quo_web_url || 'https://my.openphone.com';
  const phoneNumbers = status?.phone_numbers || [];

  return (
    <AdminSidebar pageTitle="Communications" pageIcon={Phone}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="max-w-2xl">
          <p className="text-sm text-slate-500">
            Quo (formerly OpenPhone) is your unified inbox for calls and SMS. This panel shows
            connection health and lets you look up communication history for a specific contact.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            data-testid="comms-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <a href={quoWebUrl} target="_blank" rel="noopener noreferrer">
            <Button className="bg-cyan-600 hover:bg-cyan-700" data-testid="comms-open-quo-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Quo
            </Button>
          </a>
        </div>
      </div>

      {/* Connection state */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600" />
        </div>
      ) : !isConfigured ? (
        <Card
          className="mb-6 border-amber-200 bg-amber-50"
          data-testid="comms-not-configured-banner"
        >
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Quo API key not configured</p>
              <p className="mt-1">
                Add <code className="px-1 py-0.5 bg-amber-100 rounded">QUO_API_KEY</code> to{' '}
                <code className="px-1 py-0.5 bg-amber-100 rounded">/app/backend/.env</code> and
                restart the backend.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : !isConnected ? (
        <Card
          className="mb-6 border-red-200 bg-red-50"
          data-testid="comms-connection-error-banner"
        >
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-semibold">Couldn&apos;t reach Quo</p>
              <p className="mt-1">{status?.message || 'Check that QUO_API_KEY is valid.'}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card
          className="mb-6 border-emerald-200 bg-emerald-50"
          data-testid="comms-connected-banner"
        >
          <CardContent className="pt-6 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900 flex-1">
              <p className="font-semibold">Connected to Quo</p>
              <p className="mt-1">
                {phoneNumbers.length} workspace number
                {phoneNumbers.length === 1 ? '' : 's'} active.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workspace numbers */}
      {isConnected && phoneNumbers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {phoneNumbers.map((n) => (
            <Card key={n.id} data-testid={`comms-phone-number-card-${n.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{n.name}</p>
                    <p className="text-lg font-semibold text-slate-800 mt-1">
                      {formatPhoneNumber(n.formatted_number || n.number)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-100 text-cyan-600">
                    <Phone className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Why no full feed — honest API limitation note */}
      {isConnected && (
        <Card
          className="mb-6 border-slate-200 bg-slate-50"
          data-testid="comms-api-limitation-note"
        >
          <CardContent className="pt-6 flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <p>
                <span className="font-semibold">Why not a full activity feed?</span> Quo&apos;s
                REST API requires a contact phone number to return calls or messages — there is
                no workspace-wide &quot;recent activity&quot; endpoint. For a real-time feed,
                Quo webhooks would need to mirror events into our database (Phase 2).
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-contact lookup */}
      {isConnected && (
        <Card data-testid="comms-lookup-card" className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Look up contact history</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLookup} className="flex items-center gap-2 flex-wrap mb-4">
              <Input
                value={lookupInput}
                onChange={(e) => setLookupInput(e.target.value)}
                placeholder="Phone number (e.g. +15551234567 or 555-123-4567)"
                className="max-w-md"
                data-testid="comms-lookup-input"
              />
              <Button
                type="submit"
                disabled={lookupLoading || !lookupInput}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="comms-lookup-submit"
              >
                <Search className="w-4 h-4 mr-2" />
                {lookupLoading ? 'Searching…' : 'Search'}
              </Button>
              {(lookupResult?.data || []).length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  {['all', 'calls', 'messages'].map((key) => (
                    <Button
                      key={key}
                      size="sm"
                      type="button"
                      variant={filter === key ? 'default' : 'outline'}
                      onClick={() => setFilter(key)}
                      data-testid={`comms-filter-${key}`}
                    >
                      {key === 'all' ? 'All' : key === 'calls' ? 'Calls' : 'Messages'}
                    </Button>
                  ))}
                </div>
              )}
            </form>

            {lookupLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600" />
              </div>
            ) : lookupResult ? (
              filteredFeed.length === 0 ? (
                <div
                  className="text-center py-10 text-slate-500"
                  data-testid="comms-lookup-empty"
                >
                  No {filter === 'all' ? 'history' : filter} with{' '}
                  {formatPhoneNumber(lookupResult.participant)} in the last{' '}
                  {lookupResult.window_days}d.
                </div>
              ) : (
                <div
                  className="border rounded-lg overflow-hidden"
                  data-testid="comms-lookup-results"
                >
                  {filteredFeed.map((item) => (
                    <ActivityRow key={`${item.type}-${item.id}`} item={item} />
                  ))}
                </div>
              )
            ) : (
              <div
                className="text-center py-10 text-slate-400 text-sm"
                data-testid="comms-lookup-placeholder"
              >
                Enter a contact&apos;s phone number to see their call and SMS history.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </AdminSidebar>
  );
};

export default AdminPhonePage;
